import { _ as __rest } from './_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useMemo } from 'react';
import { generateClasses } from '@ids/tools';
import { u as useId } from './use-id.js';
import { I as IdsChipsItem } from './chips-item.js';
import { s as styleInject } from './style-inject.es.js';

var css_248z = "/*\n** Voxel Foundation Formats\n*/\n.ids-chip {\n  height: 100%;\n  position: relative;\n}\n.ids-chip .ids-icon {\n  color: inherit;\n}\n.ids-chip .ids-number-badge {\n  pointer-events: none;\n}\n.ids-chip__content {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  border: var(--ids_size_border_small) solid var(--ids_color_action_secondary_base);\n  border-radius: var(--ids_size_cornerRadius_tags_02);\n  color: var(--ids_color_action_secondary_base);\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n  gap: var(--ids_size_general_8x);\n  height: 100%;\n  min-height: var(--ids_size_general_32x);\n  padding: 0 var(--ids_size_general_16x);\n  -webkit-transition: background-color 200ms linear;\n  transition: background-color 200ms linear;\n  -webkit-user-select: none;\n     -moz-user-select: none;\n      -ms-user-select: none;\n          user-select: none;\n  white-space: nowrap;\n}\n.ids-chip__icon {\n  margin-left: var(--ids_size_general_12x);\n  pointer-events: none;\n}\n.ids-chip__label {\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n  line-height: var(--ids_size_general_20x);\n  -webkit-transition: color 200ms linear;\n  transition: color 200ms linear;\n}\n.ids-chip__input {\n  -webkit-appearance: none;\n     -moz-appearance: none;\n          appearance: none;\n  border: 0;\n  cursor: pointer;\n  height: 100%;\n  position: absolute;\n  width: 100%;\n}\n.ids-chip--on-brand .ids-chip__content {\n  background-color: transparent;\n  border: var(--ids_size_border_small) solid var(--ids_color_contrast_onBrand_primary);\n  color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-chip__input:disabled {\n  cursor: not-allowed;\n}\n.ids-chip__input:checked + .ids-chip__content {\n  background-color: var(--ids_color_action_secondary_base);\n  border-color: var(--ids_color_action_secondary_variant);\n  color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-chip__input:disabled + .ids-chip__content {\n  background-color: transparent;\n  border-color: var(--ids_color_action_disabled_base);\n  color: var(--ids_color_action_disabled_variant);\n}\n.ids-chip--on-brand .ids-chip__input:checked + .ids-chip__content {\n  background-color: var(--ids_color_contrast_onBrand_primary);\n  border-color: var(--ids_color_contrast_onBrand_primary);\n  color: var(--ids_color_action_secondary_base);\n}\n.ids-chip--on-brand .ids-chip__input:disabled + .ids-chip__content {\n  border-color: var(--ids_color_contrast_onBrand_primary_medium);\n  color: var(--ids_color_contrast_onBrand_primary_medium);\n}\n.ids-chip__input:hover:not(:disabled) + .ids-chip__content {\n  background-color: var(--ids_color_action_secondary_variant);\n  border-color: var(--ids_color_action_secondary_variant);\n}\n.ids-chip__input:hover:not(:disabled, :checked) + .ids-chip__content {\n  background-color: var(--ids_color_action_secondary_base_soft);\n  border-color: var(--ids_color_action_secondary_variant);\n  color: var(--ids_color_action_secondary_variant);\n}\n.ids-chip--on-brand .ids-chip__input:hover:not(:disabled) + .ids-chip__content {\n  background-color: var(--ids_color_contrast_onBrand_primary_strong);\n  border-color: var(--ids_color_contrast_onBrand_primary);\n}\n.ids-chip--on-brand .ids-chip__input:hover:not(:disabled, :checked) + .ids-chip__content {\n  background-color: var(--ids_color_contrast_onBrand_primary_soft);\n  color: var(--ids_color_contrast_onBrand_primary);\n}\n\n.ids-chips__legend {\n  font: var(--ids_textStyle_f02_s16_h24_wrg);\n  margin-bottom: var(--ids_size_general_8x);\n}\n.ids-chips__options {\n  -webkit-box-align: stretch;\n      -ms-flex-align: stretch;\n          align-items: stretch;\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  -ms-flex-wrap: wrap;\n      flex-wrap: wrap;\n  gap: var(--ids_size_general_8x);\n}\n.ids-chips--on-brand {\n  color: var(--ids_color_contrast_onBrand_primary);\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsChips } from '@ids/react';
 * ```
 */
const IdsChips = _a => {
  var {
      id: externalId,
      legend,
      contrast,
      setOptions: externalSetOptions,
      options: externalOptions,
      type,
      onChange,
      children,
      className = ''
    } = _a,
    props = __rest(_a, ["id", "legend", "contrast", "setOptions", "options", "type", "onChange", "children", "className"]);
  const id = useId(externalId, 'ids-chips');
  const [internalOptions, setInternalOptions] = useState(externalOptions);
  const setOptions = externalSetOptions !== null && externalSetOptions !== void 0 ? externalSetOptions : setInternalOptions;
  const classes = useMemo(() => generateClasses({
    'ids-chips': true,
    'ids-chips--on-brand': contrast === 'onBrand',
    [className]: className
  }), [contrast, className]);
  const list = useMemo(() => {
    const options = (externalSetOptions ? externalOptions : internalOptions) || [];
    const handleChange = event => {
      const target = event.target;
      const index = options.findIndex(option => option.value === target.value);
      const optionsToEmit = [...options];
      const optionsToEdit = Object.assign(Object.assign({}, options[index]), {
        checked: target.checked
      });
      optionsToEmit[index] = optionsToEdit;
      setOptions(optionsToEmit);
      if (onChange) {
        onChange(optionsToEmit);
      }
    };
    function renderItem(_a) {
      var {
          checked = false
        } = _a,
        option = __rest(_a, ["checked"]);
      return jsx("div", Object.assign({
        className: "ids-chips__item"
      }, {
        children: jsx(IdsChipsItem, Object.assign({
          type: type,
          contrast: contrast,
          onChange: handleChange,
          name: `${id}-selection`,
          checked: checked
        }, option))
      }), String(option.value));
    }
    return options.map(renderItem);
  }, [contrast, externalOptions, externalSetOptions, type, id, internalOptions, onChange, setOptions]);
  return jsxs("fieldset", Object.assign({
    className: classes,
    id: id
  }, props, {
    children: [(legend || children) && jsx("legend", Object.assign({
      className: "ids-chips__legend"
    }, {
      children: legend || children
    })), jsx("div", Object.assign({
      className: "ids-chips__options"
    }, {
      children: list
    }))]
  }));
};

export { IdsChips as I };
//# sourceMappingURL=chips.js.map
